import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useMediaQuery } from 'react-responsive';

export const THEMES = ['system', 'dark', 'light'] as const;

export type Theme = (typeof THEMES)[number];

const getDefaultTheme = () => {
  const saved = localStorage.getItem('theme') as Theme;
  if (THEMES.includes(saved)) return saved;

  return 'system';
};

const toggleTheme = (isDark: boolean) => {
  if (isDark) {
    document.documentElement.setAttribute('data-mode', 'dark');
  } else {
    document.documentElement.removeAttribute('data-mode');
  }
};

export interface ThemeContextType {
  theme: Theme;
  changeTheme: (theme: Theme) => void;
  isDark: boolean;
}

const ThemeContext = createContext<ThemeContextType>({
  theme: 'system',
  changeTheme: () => undefined,
  isDark: false,
});

export const ThemeProvider = ({ children }: { children: ReactNode }) => {
  const [theme, setTheme] = useState(() => getDefaultTheme());

  const changeTheme = useCallback((value: Theme) => {
    setTheme(value);
    localStorage.setItem('theme', value);
  }, []);

  const systemPrefersDark = useMediaQuery(
    {
      query: '(prefers-color-scheme: dark)',
    },
    undefined,
    (prefersDark) => toggleTheme(prefersDark)
  );

  const isDark = useMemo(() => {
    return theme === 'system' ? systemPrefersDark : theme === 'dark';
  }, [systemPrefersDark, theme]);

  const value = useMemo(
    () => ({ theme, changeTheme, isDark }),
    [changeTheme, theme, isDark]
  );

  useEffect(() => {
    if (theme === 'system') toggleTheme(systemPrefersDark);
    else toggleTheme(theme === 'dark');

    return () => toggleTheme(false);
  }, [theme, systemPrefersDark]);

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
};

export const useAppTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useAppTheme must be used within a ThemeProvider');
  }

  return context;
};
